import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Playlist, PlaylistStore } from './types';
import { nanoid } from 'nanoid';

// Função auxiliar para limpar storage antigo quando quota é excedida
function cleanupOldStorage() {
  const keysToClean = [
    'video-features-temp-autosave',
    'old-cache',
    'temp-data',
  ];
  keysToClean.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Erro ao limpar ${key}:`, e);
    }
  });
}

export const usePlaylistStore = create<PlaylistStore>()(
  persist(
    (set, get) => ({
      playlists: [],
      currentPlaylist: null,
      isLoading: false,

      setPlaylists: (playlists) => set({ playlists }),

      addPlaylist: (playlist) =>
        set((state) => ({
          playlists: [...state.playlists, playlist],
        })),

      updatePlaylist: (id, updates) =>
        set((state) => ({
          playlists: state.playlists.map((p) =>
            p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
          ),
        })),

      deletePlaylist: (id) =>
        set((state) => ({
          playlists: state.playlists.filter((p) => p.id !== id),
          currentPlaylist: state.currentPlaylist?.id === id ? null : state.currentPlaylist,
        })),

      duplicatePlaylist: (id) =>
        set((state) => {
          const original = state.playlists.find((p) => p.id === id);
          if (!original) return state;

          const duplicate: Playlist = {
            ...original,
            id: nanoid(),
            name: `${original.name} (cópia)`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            publicHash: nanoid(10),
            theme: 'blue',
            template: 'netflix',
          };

          return { playlists: [...state.playlists, duplicate] };
        }),

      setCurrentPlaylist: (playlist) => set({ currentPlaylist: playlist }),

      getPlaylistById: (id) => {
        return get().playlists.find((p) => p.id === id);
      },

      getPlaylistByHash: (hash) => {
        return get().playlists.find((p) => p.publicHash === hash);
      },
    }),
    {
      name: 'playlist-storage',
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          try {
            return localStorage.getItem(name);
          } catch (error) {
            console.error('❌ Erro ao ler playlist do localStorage:', error);
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, value);
          } catch (error) {
            // Detectar erro de quota excedida
            if (
              error instanceof DOMException &&
              (error.code === 22 || 
               error.name === 'QuotaExceededError' ||
               error.message?.includes('quota'))
            ) {
              console.error('❌ localStorage cheio! Tentando limpar dados antigos...');
              
              // Tentar limpar dados antigos
              cleanupOldStorage();
              
              // Tentar salvar novamente após limpeza
              try {
                localStorage.setItem(name, value);
                console.log('✅ Salvo com sucesso após limpeza');
              } catch (retryError) {
                console.error('❌ Falha ao salvar mesmo após limpeza:', retryError);
                throw retryError; // Re-throw para ser capturado pelo autosave
              }
            } else {
              console.error('❌ Erro desconhecido ao salvar:', error);
              throw error;
            }
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name);
          } catch (error) {
            console.error('❌ Erro ao remover do localStorage:', error);
          }
        },
      })),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('❌ Erro ao carregar playlists do localStorage:', error);
        } else {
          console.log('✅ Playlists carregadas com sucesso');
        }
      },
    }
  )
);
